import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Snackbar } from "@mui/material";
import {
  fetchChatConversation,
  fetchChatConversations,
  fetchTeamsStatus,
} from "../../api/ProcurementClient";
import { ChatSocketEvent, useChatSocket } from "../../hooks/useChatSocket";
import { ChatConversation, TeamsStatus } from "../../models/Chat";

type ChatEventListener = (event: ChatSocketEvent) => void;

export interface ChatSidebarState {
  open: boolean;
  conversationId: number | null;
  threadId: number | null;
  width: number;
}

const CHAT_SIDEBAR_MIN_WIDTH = 320;
const CHAT_SIDEBAR_MAX_WIDTH = 720;

export function clampSidebarWidth(width: number): number {
  return Math.min(CHAT_SIDEBAR_MAX_WIDTH, Math.max(CHAT_SIDEBAR_MIN_WIDTH, width));
}
const CHAT_SIDEBAR_STORAGE_KEY = "chatSidebar";
const DEFAULT_SIDEBAR: ChatSidebarState = {
  open: false,
  conversationId: null,
  threadId: null,
  width: 400,
};

function loadSidebarState(): ChatSidebarState {
  try {
    const raw = localStorage.getItem(CHAT_SIDEBAR_STORAGE_KEY);
    if (!raw) return DEFAULT_SIDEBAR;
    const parsed = JSON.parse(raw) as Partial<ChatSidebarState>;
    const width = Number(parsed.width);
    return {
      open: Boolean(parsed.open),
      conversationId: Number(parsed.conversationId) > 0 ? Number(parsed.conversationId) : null,
      threadId: Number(parsed.threadId) > 0 ? Number(parsed.threadId) : null,
      width: Number.isFinite(width) ? clampSidebarWidth(width) : DEFAULT_SIDEBAR.width,
    };
  } catch {
    return DEFAULT_SIDEBAR;
  }
}

interface ChatContextValue {
  sidebar: ChatSidebarState;
  updateSidebar: (changes: Partial<ChatSidebarState>) => void;
  /** Null until loaded, or when Teams sync is off on the server. */
  teams: TeamsStatus | null;
  setTeams: (status: TeamsStatus) => void;
  enabled: boolean;
  conversations: ChatConversation[];
  loading: boolean;
  error: string | null;
  totalUnread: number;
  totalMentions: number;
  refreshConversations: () => Promise<void>;
  upsertConversation: (conversation: ChatConversation) => void;
  clearUnread: (conversationId: number) => void;
  subscribe: (listener: ChatEventListener) => () => void;
}

const noop = () => {};

const ChatContext = createContext<ChatContextValue>({
  sidebar: DEFAULT_SIDEBAR,
  updateSidebar: noop,
  teams: null,
  setTeams: noop,
  enabled: false,
  conversations: [],
  loading: false,
  error: null,
  totalUnread: 0,
  totalMentions: 0,
  refreshConversations: async () => {},
  upsertConversation: noop,
  clearUnread: noop,
  subscribe: () => noop,
});

export const useChat = () => useContext(ChatContext);

/**
 * The conversation with this id, loading it if the list doesn't have it yet
 * (e.g. a channel you haven't joined). `missing` once the server says it's gone.
 */
export function useEnsureConversation(
  conversationId: number | null,
  onMissing?: () => void
): { conversation: ChatConversation | null; missing: boolean } {
  const { conversations, loading, upsertConversation } = useContext(ChatContext);
  const conversation = conversations.find((c) => c.id === conversationId) ?? null;
  const [missingId, setMissingId] = useState<number | null>(null);
  const onMissingRef = useRef(onMissing);
  onMissingRef.current = onMissing;

  useEffect(() => {
    if (!conversationId || conversation || loading || missingId === conversationId) return;
    let cancelled = false;
    fetchChatConversation(conversationId).then(
      (loaded) => {
        if (!cancelled) upsertConversation(loaded);
      },
      () => {
        if (cancelled) return;
        setMissingId(conversationId);
        onMissingRef.current?.();
      }
    );
    return () => {
      cancelled = true;
    };
  }, [conversationId, conversation, loading, missingId, upsertConversation]);

  return { conversation, missing: conversationId !== null && missingId === conversationId };
}

const REFRESH_DEBOUNCE_MS = 400;

/** Same order as the server: most recent activity first. */
function sortByActivity(conversations: ChatConversation[]): ChatConversation[] {
  const activity = (c: ChatConversation) => Date.parse(c.lastMessageAt ?? c.createdAt) || 0;
  return conversations.sort((a, b) => activity(b) - activity(a) || b.id - a.id);
}

const ChatProvider: React.FC<{ enabled: boolean; children: React.ReactNode }> = ({
  enabled,
  children,
}) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listenersRef = useRef(new Set<ChatEventListener>());
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const [sidebar, setSidebar] = useState<ChatSidebarState>(loadSidebarState);
  const [teams, setTeams] = useState<TeamsStatus | null>(null);
  const [teamsNotice, setTeamsNotice] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    setTeams(null);
    if (!enabled) return;
    // A failed status load just hides the Teams UI; chat itself keeps working.
    fetchTeamsStatus().then(setTeams, () => undefined);
  }, [enabled]);

  // Microsoft sign-in returns here with ?teams=connected|error; show it once and clean the URL.
  useEffect(() => {
    if (!enabled) return;
    const url = new URL(window.location.href);
    const result = url.searchParams.get("teams");
    if (!result) return;
    setTeamsNotice(
      result === "connected"
        ? { severity: "success", message: "Microsoft Teams connected. Your direct messages will sync." }
        : {
            severity: "error",
            message: `Teams connection failed: ${url.searchParams.get("teamsMessage") || "unknown error"}`,
          }
    );
    url.searchParams.delete("teams");
    url.searchParams.delete("teamsMessage");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, [enabled]);

  const updateSidebar = useCallback((changes: Partial<ChatSidebarState>) => {
    setSidebar((prev) => {
      const next = { ...prev, ...changes };
      try {
        localStorage.setItem(CHAT_SIDEBAR_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage full or disabled; state still works for this session */
      }
      return next;
    });
  }, []);

  const refreshConversations = useCallback(async () => {
    if (!enabled) return;
    try {
      const rows = await fetchChatConversations();
      setConversations(rows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load conversations");
    }
  }, [enabled]);

  const scheduleRefresh = useCallback(() => {
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      void refreshConversations();
    }, REFRESH_DEBOUNCE_MS);
  }, [refreshConversations]);

  useEffect(() => {
    if (!enabled) {
      setConversations([]);
      return;
    }
    setLoading(true);
    void refreshConversations().finally(() => setLoading(false));
    return () => window.clearTimeout(refreshTimerRef.current);
  }, [enabled, refreshConversations]);

  const upsertConversation = useCallback((conversation: ChatConversation) => {
    setConversations((prev) =>
      sortByActivity([...prev.filter((c) => c.id !== conversation.id), conversation])
    );
  }, []);

  const clearUnread = useCallback((conversationId: number) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId && (c.unreadCount > 0 || c.mentionCount > 0)
          ? { ...c, unreadCount: 0, mentionCount: 0 }
          : c
      )
    );
  }, []);

  // Socket events touch one conversation; refetch just those (batched) instead of the whole list.
  const pendingIdsRef = useRef(new Set<number>());
  const pendingTimerRef = useRef<number | undefined>(undefined);
  const scheduleConversationRefresh = useCallback(
    (conversationId: number) => {
      pendingIdsRef.current.add(conversationId);
      window.clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = window.setTimeout(() => {
        const ids = Array.from(pendingIdsRef.current);
        pendingIdsRef.current.clear();
        for (const id of ids) {
          // Left or deleted conversations 404; the full list drops them.
          fetchChatConversation(id).then(upsertConversation, scheduleRefresh);
        }
      }, REFRESH_DEBOUNCE_MS);
    },
    [scheduleRefresh, upsertConversation]
  );

  const handleEvent = useCallback(
    (event: ChatSocketEvent) => {
      listenersRef.current.forEach((listener) => listener(event));
      switch (event.type) {
        case "message":
          scheduleConversationRefresh(event.message.conversationId);
          break;
        case "conversation-updated":
          scheduleConversationRefresh(event.conversationId);
          break;
        case "read":
          clearUnread(event.conversationId);
          break;
        case "connected":
          scheduleRefresh();
          break;
      }
    },
    [scheduleConversationRefresh, scheduleRefresh, clearUnread]
  );

  useChatSocket(enabled, handleEvent);

  const subscribe = useCallback((listener: ChatEventListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const value = useMemo<ChatContextValue>(() => {
    const counted = conversations.filter((c) => c.joined && !c.archivedAt);
    return {
      sidebar: enabled ? sidebar : DEFAULT_SIDEBAR,
      updateSidebar,
      teams: enabled ? teams : null,
      setTeams,
      enabled,
      conversations,
      loading,
      error,
      totalUnread: counted.reduce((sum, c) => sum + c.unreadCount, 0),
      totalMentions: counted.reduce((sum, c) => sum + c.mentionCount, 0),
      refreshConversations,
      upsertConversation,
      clearUnread,
      subscribe,
    };
  }, [
    sidebar,
    updateSidebar,
    teams,
    enabled,
    conversations,
    loading,
    error,
    refreshConversations,
    upsertConversation,
    clearUnread,
    subscribe,
  ]);

  return (
    <ChatContext.Provider value={value}>
      {children}
      {teamsNotice && (
        <Snackbar
          open
          autoHideDuration={teamsNotice.severity === "error" ? null : 6000}
          onClose={() => setTeamsNotice(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={teamsNotice.severity} onClose={() => setTeamsNotice(null)} variant="filled">
            {teamsNotice.message}
          </Alert>
        </Snackbar>
      )}
    </ChatContext.Provider>
  );
};

export default ChatProvider;
